import * as RDialog from '@radix-ui/react-dialog';
import * as RDropdown from '@radix-ui/react-dropdown-menu';
import * as RTabs from '@radix-ui/react-tabs';
import * as RTooltip from '@radix-ui/react-tooltip';
import clsxBase, { type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Check, ChevronRight, Loader2, MoreHorizontal, X, type LucideIcon } from 'lucide-react';
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Link } from 'react-router';

/** clsx + tailwind-merge, so a className passed in overrides the defaults (e.g. w-auto over w-full) */
export const clsx = (...v: ClassValue[]) => twMerge(clsxBase(v));
export const cn = clsx;

/* ------------------------------------------------------------------ */
/* Buttons                                                             */
/* ------------------------------------------------------------------ */

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

/** Buttons are 36 px on desktop and 44 px on phones (the touch-target minimum); `sm` stays 32 px everywhere */
export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; busy?: boolean; size?: 'sm' | 'md' | 'icon' }
>(({ variant = 'secondary', busy, size = 'md', className, children, disabled, ...rest }, ref) => (
  <button
    ref={ref}
    disabled={disabled || busy}
    className={clsx(
      'inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50',
      size === 'sm' && 'h-8 px-2.5 text-sm',
      size === 'md' && 'h-9 px-3.5 text-sm max-sm:h-11 max-sm:px-4',
      size === 'icon' && 'size-9 max-sm:size-11',
      variant === 'primary' && 'bg-teal text-on-teal shadow-sm hover:bg-teal-dark',
      variant === 'secondary' && 'border border-line-strong bg-surface text-ink shadow-sm hover:bg-paper',
      variant === 'ghost' && 'text-ink-soft hover:bg-ink/5 hover:text-ink',
      variant === 'danger' && 'border border-danger/30 bg-surface text-danger hover:bg-danger-wash',
      className,
    )}
    {...rest}
  >
    {busy && <Loader2 className="size-4 animate-spin" aria-hidden />}
    {children}
  </button>
));
Button.displayName = 'Button';

/* ------------------------------------------------------------------ */
/* Form controls                                                       */
/* ------------------------------------------------------------------ */

const control =
  'h-9 w-full rounded-md border border-line-strong bg-surface px-2.5 text-base text-ink shadow-sm placeholder:text-ink-faint focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20 disabled:bg-paper disabled:text-ink-faint disabled:shadow-none aria-[invalid=true]:border-danger max-sm:h-11';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ className, ...rest }, ref) => (
  <input ref={ref} className={clsx(control, className)} {...rest} />
));
Input.displayName = 'Input';

/** A number field that opens the numeric keyboard on phones */
export const NumberInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { decimal?: boolean }>(
  ({ decimal, className, ...rest }, ref) => (
    <input ref={ref} inputMode={decimal ? 'decimal' : 'numeric'} className={clsx(control, 'num text-right', className)} {...rest} />
  ),
);
NumberInput.displayName = 'NumberInput';

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(({ className, ...rest }, ref) => (
  <select ref={ref} className={clsx(control, 'pr-8', className)} {...rest} />
));
Select.displayName = 'Select';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...rest }, ref) => (
  <textarea ref={ref} className={clsx(control, 'h-auto min-h-20 py-2', className)} {...rest} />
));
Textarea.displayName = 'Textarea';

export function Field({
  label,
  error,
  hint,
  children,
  className,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={clsx('block', className)}>
      <span className="mb-1 block text-sm font-medium text-ink-soft">{label}</span>
      {children}
      {error ? <span className="mt-1 block text-sm text-danger">{error}</span> : hint ? <span className="mt-1 block text-xs text-ink-faint">{hint}</span> : null}
    </label>
  );
}

/* ------------------------------------------------------------------ */
/* Badge and status                                                    */
/* ------------------------------------------------------------------ */

export type Tone = 'neutral' | 'teal' | 'amber' | 'violet' | 'danger' | 'success';

const toneClass: Record<Tone, string> = {
  neutral: 'bg-ink/6 text-ink-soft',
  teal: 'bg-teal-wash text-teal-dark',
  amber: 'bg-amber-wash text-amber',
  violet: 'bg-violet-wash text-violet',
  danger: 'bg-danger-wash text-danger',
  success: 'bg-success-wash text-success',
};
const dotClass: Record<Tone, string> = {
  neutral: 'bg-ink-faint',
  teal: 'bg-teal',
  amber: 'bg-amber',
  violet: 'bg-violet',
  danger: 'bg-danger',
  success: 'bg-success',
};

/** A label with a tinted background; `dot` adds a colour dot for status values so colour is not the only cue */
export function Badge({ tone = 'neutral', dot, children, className }: { tone?: Tone; dot?: boolean; children: ReactNode; className?: string }) {
  return (
    <span className={clsx('inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-xs font-medium whitespace-nowrap', toneClass[tone], className)}>
      {dot && <span className={clsx('size-1.5 shrink-0 rounded-full', dotClass[tone])} aria-hidden />}
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Page structure                                                      */
/* ------------------------------------------------------------------ */

export function PageHeader({
  title,
  description,
  actions,
  back,
  eyebrow,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Link to the list this record came from; shows a back chevron on phones and a breadcrumb on desktop */
  back?: { to: string; label: string };
  eyebrow?: ReactNode;
}) {
  return (
    <div className="relative mb-5 flex flex-wrap items-end justify-between gap-3 max-sm:mb-4">
      <div className={clsx('min-w-0 max-sm:w-full', back && actions && 'max-sm:pr-14')}>
        {back && (
          <Link to={back.to} className="mb-1 inline-flex items-center gap-1 text-sm text-ink-soft hover:text-ink">
            <ChevronRight className="size-4 rotate-180" aria-hidden />
            {back.label}
          </Link>
        )}
        {eyebrow && <div className="mb-0.5 text-xs font-medium tracking-wide text-ink-faint uppercase">{eyebrow}</div>}
        <h1 className="text-xl font-semibold tracking-tight break-words">{title}</h1>
        {description && <p className="mt-0.5 max-w-prose text-sm text-ink-soft">{description}</p>}
      </div>
      {actions &&
        (back ? (
          // a record page: its actions menu sits top right on phones instead of taking a row of its own
          <div className="flex flex-wrap items-center gap-2 max-sm:absolute max-sm:top-0 max-sm:right-0">{actions}</div>
        ) : (
          <div className="flex flex-wrap items-center gap-2 max-sm:w-full max-sm:[&>*]:flex-1">{actions}</div>
        ))}
    </div>
  );
}

/**
 * Search and filters above a list. On phones the filters sit two to a row at full width (an odd last one
 * takes the whole row) instead of a ragged stack of differently sized selects; the search box and anything
 * marked `w-full` get a row of their own.
 */
export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={clsx(
        'mb-3 flex flex-wrap items-center gap-2',
        'max-sm:[&>*]:!w-auto max-sm:[&>*]:min-w-0 max-sm:[&>*]:grow max-sm:[&>*]:basis-[calc(50%-0.25rem)] max-sm:[&>.w-full]:basis-full',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx('rounded-lg border border-line bg-surface shadow-sm', className)}>{children}</div>;
}

/** Primary action bar that stays above the fold on phones (sits above the safe area) */
export function StickyActions({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={clsx(
        'sticky bottom-[var(--tabbar-h)] z-30 -mx-4 mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-line bg-surface/95 px-4 py-3 backdrop-blur sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:backdrop-blur-none',
        'max-sm:[&>*]:flex-1',
        className,
      )}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tables                                                              */
/* ------------------------------------------------------------------ */

export function Table({ children, className, dense }: { children: ReactNode; className?: string; dense?: boolean }) {
  return (
    <div className={clsx('overflow-x-auto rounded-lg border border-line bg-surface shadow-sm', className)}>
      <table className={clsx('w-full border-collapse text-sm', dense && 'table-dense')}>{children}</table>
    </div>
  );
}

export function Th({ children, className, align = 'left' }: { children?: ReactNode; className?: string; align?: 'left' | 'right' | 'center' }) {
  return (
    <th
      className={clsx(
        'sticky top-0 z-10 border-b border-line bg-surface px-3 py-2 text-xs font-medium whitespace-nowrap text-ink-faint',
        align === 'left' && 'text-left',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({ children, className, align = 'left' }: { children?: ReactNode; className?: string; align?: 'left' | 'right' | 'center' }) {
  return (
    <td className={clsx('border-b border-line px-3 py-2 align-middle', align === 'right' && 'text-right', align === 'center' && 'text-center', className)}>
      {children}
    </td>
  );
}

export function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-3 py-10 text-center text-sm text-ink-soft">
        {children}
      </td>
    </tr>
  );
}

/**
 * A list that is a table on tablets and desktops and a card list on phones.
 * `table` renders the <thead>/<tbody>; `card` renders one row as a card. Both get the same rows,
 * so pages define the data once. Loading shows skeletons in whichever shape is on screen.
 */
export function ResponsiveTable<T>({
  rows,
  table,
  card,
  keyOf,
  loading,
  empty,
  columns,
  className,
  cardClassName,
}: {
  rows: T[];
  table: (rows: T[]) => ReactNode;
  card: (row: T, index: number) => ReactNode;
  keyOf: (row: T) => string;
  loading?: boolean;
  empty?: ReactNode;
  /** Column count for the skeleton rows */
  columns?: number;
  className?: string;
  cardClassName?: string;
}) {
  if (loading && !rows.length) {
    return (
      <>
        <div className="md:hidden">
          <SkeletonCards />
        </div>
        <div className="hidden md:block">
          <SkeletonTable columns={columns ?? 5} />
        </div>
      </>
    );
  }
  if (!rows.length) {
    // pages usually pass a ready-made <EmptyState>; a plain string gets the default one
    return typeof empty === 'string' || empty == null ? <EmptyState>{empty ?? 'Nothing here yet.'}</EmptyState> : <>{empty}</>;
  }
  return (
    <>
      <ul className={clsx('list-lazy flex flex-col gap-2 md:hidden', cardClassName)}>
        {rows.map((r, i) => (
          <li key={keyOf(r)}>{card(r, i)}</li>
        ))}
      </ul>
      <div className={clsx('hidden md:block', className)}>{table(rows)}</div>
    </>
  );
}

/** One record as a card: title row, facts underneath, optional status on the right. Tapping opens `to`. */
export function RowCard({
  title,
  subtitle,
  status,
  facts,
  to,
  onClick,
  leading,
  trailing,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  status?: ReactNode;
  /** Label → value pairs shown as a two-column grid; values wrap to two lines, `wide` ones take the full width and wrap freely */
  facts?: { label: string; value: ReactNode; wide?: boolean }[];
  to?: string;
  onClick?: () => void;
  leading?: ReactNode;
  trailing?: ReactNode;
  className?: string;
}) {
  const body = (
    <>
      {leading && <div className="shrink-0">{leading}</div>}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="line-clamp-2 font-medium break-words text-ink">{title}</div>
            {subtitle && <div className="line-clamp-2 text-sm break-words text-ink-soft">{subtitle}</div>}
          </div>
          {status && <div className="shrink-0">{status}</div>}
        </div>
        {facts && facts.length > 0 && (
          <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
            {facts.map((f) => (
              <div key={f.label} className={clsx('min-w-0', f.wide && 'col-span-2')}>
                <dt className="text-xs text-ink-faint">{f.label}</dt>
                <dd className={clsx('break-words text-ink', !f.wide && 'line-clamp-2')}>{f.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
      {trailing ?? ((to || onClick) && <ChevronRight className="size-5 shrink-0 self-center text-ink-faint" aria-hidden />)}
    </>
  );
  const cls = clsx('flex w-full gap-3 rounded-lg border border-line bg-surface p-3 text-left shadow-sm transition-colors', (to || onClick) && 'active:bg-paper', className);
  if (to) return <Link to={to} className={cls}>{body}</Link>;
  if (onClick)
    return (
      <button type="button" onClick={onClick} className={cls}>
        {body}
      </button>
    );
  return <div className={cls}>{body}</div>;
}

export function LoadMore({ hasMore, loading, onClick }: { hasMore: boolean; loading: boolean; onClick: () => void }) {
  if (!hasMore) return null;
  return (
    <div className="mt-3 flex justify-center">
      <Button onClick={onClick} busy={loading}>
        Show more
      </Button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Feedback                                                            */
/* ------------------------------------------------------------------ */

export function ErrorBanner({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <div role="alert" className="mb-3 rounded-md border border-danger/25 bg-danger-wash px-3 py-2 text-sm text-danger">{children}</div>;
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-sm text-ink-soft" role="status">
      <Loader2 className="size-4 animate-spin" aria-hidden /> {label}
    </div>
  );
}

/** A grey shimmer block the size of the content it stands in for */
export function Skeleton({ className }: { className?: string }) {
  return <div className={clsx('skeleton rounded-md', className)} aria-hidden />;
}

export function SkeletonTable({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="overflow-hidden rounded-lg border border-line bg-surface" role="status" aria-label="Loading">
      <div className="flex gap-6 border-b border-line px-3 py-2.5">
        {Array.from({ length: columns }, (_, i) => (
          <Skeleton key={i} className="h-3 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex gap-6 border-b border-line px-3 py-3 last:border-0">
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton key={c} className={clsx('h-3.5 flex-1', c === 0 && 'max-w-40')} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function SkeletonCards({ count = 4 }: { count?: number }) {
  return (
    <div className="flex flex-col gap-2" role="status" aria-label="Loading">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="rounded-lg border border-line bg-surface p-3">
          <div className="flex justify-between gap-3">
            <Skeleton className="h-4 w-2/5" />
            <Skeleton className="h-4 w-16" />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** A friendly empty list: short message and, ideally, the one action that fills it */
export function EmptyState({ icon: Icon, title, children, action, className }: { icon?: LucideIcon; title?: ReactNode; children?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={clsx('flex flex-col items-center justify-center rounded-lg border border-dashed border-line-strong px-4 py-12 text-center', className)}>
      {Icon && (
        <div className="mb-3 flex size-10 items-center justify-center rounded-full bg-paper text-ink-faint">
          <Icon className="size-5" aria-hidden />
        </div>
      )}
      {title && <div className="font-medium text-ink">{title}</div>}
      {children && <div className="mt-1 max-w-sm text-sm text-ink-soft">{children}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Dialog                                                              */
/* ------------------------------------------------------------------ */

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  wide,
  fullscreen,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  /** Takes the whole screen on phones and most of it on desktop, capped at 1280 px wide and centred on large screens: for import previews and other dialogs with their own tables */
  fullscreen?: boolean;
}) {
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-40 bg-overlay" />
        <RDialog.Content
          onOpenAutoFocus={(e) => {
            if (window.matchMedia('(pointer: coarse)').matches) {
              e.preventDefault();
              (e.currentTarget as HTMLElement).focus();
            }
          }}
          className={clsx(
            'fixed z-50 flex flex-col bg-surface shadow-xl focus:outline-none',
            fullscreen
              ? 'inset-0 sm:inset-4 sm:rounded-xl lg:inset-8 lg:mx-auto lg:w-auto lg:max-w-7xl'
              : 'inset-x-0 bottom-0 max-h-[92dvh] rounded-t-xl sm:inset-auto sm:top-1/2 sm:left-1/2 sm:w-[calc(100vw-2rem)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl',
            !fullscreen && (wide ? 'sm:max-w-3xl' : 'sm:max-w-lg'),
          )}
        >
          {!fullscreen && <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-line-strong sm:hidden" aria-hidden />}
          <div className={clsx('flex items-start justify-between gap-4 border-b border-line px-5 py-4 max-sm:px-4', fullscreen && 'max-sm:pt-[max(1rem,env(safe-area-inset-top))]')}>
            <div className="min-w-0">
              <RDialog.Title className="text-lg font-semibold break-words">{title}</RDialog.Title>
              {description ? (
                <RDialog.Description className="mt-0.5 text-sm text-ink-soft">{description}</RDialog.Description>
              ) : (
                <RDialog.Description className="sr-only">{title}</RDialog.Description>
              )}
            </div>
            <RDialog.Close asChild>
              <button className="-m-2 flex size-tap items-center justify-center rounded-md text-ink-faint hover:bg-ink/5 hover:text-ink sm:size-8 sm:m-0" aria-label="Close">
                <X className="size-5" />
              </button>
            </RDialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4 max-sm:px-4">{children}</div>
          {footer && (
            <div className="flex justify-end gap-2 border-t border-line px-5 py-3 max-sm:px-4 max-sm:pb-[calc(0.75rem+env(safe-area-inset-bottom))] max-sm:[&>*]:flex-1">{footer}</div>
          )}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

/* ------------------------------------------------------------------ */
/* Tabs                                                                */
/* ------------------------------------------------------------------ */

export interface TabItem {
  value: string;
  label: ReactNode;
  count?: number;
}

const tabClass =
  '-mb-px flex h-tap shrink-0 cursor-pointer items-center gap-1.5 border-b-2 border-transparent px-3 text-sm font-medium whitespace-nowrap text-ink-soft transition-colors hover:text-ink data-[state=active]:border-teal data-[state=active]:text-ink sm:h-10';
const tabListClass = 'scroll-strip scroll-fade -mx-4 mb-4 flex gap-1 overflow-x-auto border-b border-line px-4 sm:mx-0 sm:px-0';

function TabLabel({ t }: { t: TabItem }) {
  return (
    <>
      {t.label}
      {t.count != null && <span className="num rounded-full bg-ink/6 px-1.5 py-0.5 text-xs text-ink-soft">{t.count}</span>}
    </>
  );
}

/**
 * Underlined tabs; scroll sideways on phones rather than wrapping.
 * With `children` (TabPanel) they are real tabs. Without children they act as a filter over the content
 * below (e.g. order status), so they render as a group of toggle buttons instead: tab roles would point
 * at panels that don't exist.
 */
export function Tabs({ value, onChange, items, children, className, label = 'Sections' }: { value: string; onChange: (v: string) => void; items: TabItem[]; children?: ReactNode; className?: string; label?: string }) {
  if (!children) {
    return (
      <div role="group" aria-label={label} className={clsx(tabListClass, className)}>
        {items.map((t) => (
          <button key={t.value} type="button" aria-pressed={t.value === value} data-state={t.value === value ? 'active' : 'inactive'} onClick={() => onChange(t.value)} className={tabClass}>
            <TabLabel t={t} />
          </button>
        ))}
      </div>
    );
  }
  return (
    <RTabs.Root value={value} onValueChange={onChange} className={className}>
      <RTabs.List className={tabListClass} aria-label={label}>
        {items.map((t) => (
          <RTabs.Trigger key={t.value} value={t.value} className={tabClass}>
            <TabLabel t={t} />
          </RTabs.Trigger>
        ))}
      </RTabs.List>
      {children}
    </RTabs.Root>
  );
}
export const TabPanel = RTabs.Content;

/** A few choices shown side by side (segmented control); for 2–5 options such as a status filter */
export function Segmented<T extends string>({ value, onChange, options, className, label }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode }[]; className?: string; label?: string }) {
  return (
    <div role="group" aria-label={label} className={clsx('inline-flex h-9 rounded-md border border-line-strong bg-paper p-0.5 max-sm:h-11', className)}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
          className={clsx(
            'cursor-pointer rounded-[5px] px-3 text-sm font-medium whitespace-nowrap transition-colors max-sm:flex-1',
            o.value === value ? 'bg-surface text-ink shadow-sm' : 'text-ink-soft hover:text-ink',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Progress                                                            */
/* ------------------------------------------------------------------ */

/**
 * Where a record is in its life: done steps get a tick, the current one is filled, later ones are hollow.
 * On phones only the current step's name is spelled out (with "Step 3 of 6"), so the strip never scrolls.
 */
export function Stepper({ steps, current, stopped, label = 'Progress', className }: { steps: string[]; current: number; stopped?: string; label?: string; className?: string }) {
  return (
    <div className={clsx('mb-5', className)}>
      <ol className="flex items-center" aria-label={label}>
        {steps.map((s, i) => {
          const done = !stopped && i < current;
          const now = !stopped && i === current;
          // current === steps.length: finished, every step ticked
          return (
            <li key={s} className={clsx('flex min-w-0 items-center', i < steps.length - 1 && 'flex-1')} aria-current={now ? 'step' : undefined}>
              <span
                className={clsx(
                  'flex size-6 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-semibold num',
                  done && 'border-teal bg-teal text-on-teal',
                  now && 'border-teal bg-teal-wash text-teal-dark',
                  !done && !now && 'border-line-strong bg-surface text-ink-faint',
                )}
              >
                {done ? <Check className="size-3.5" strokeWidth={3} aria-hidden /> : i + 1}
              </span>
              <span className={clsx('ml-2 truncate text-xs whitespace-nowrap max-md:hidden', now ? 'font-medium text-ink' : done ? 'text-ink-soft' : 'text-ink-faint')}>{s}</span>
              {i < steps.length - 1 && <span className={clsx('mx-2 h-0.5 min-w-3 flex-1 rounded-full', done ? 'bg-teal' : 'bg-line')} aria-hidden />}
            </li>
          );
        })}
      </ol>
      <p className="mt-2 text-xs text-ink-soft md:hidden">
        {stopped ? (
          <span className="text-danger">{stopped}</span>
        ) : (
          <>
            {current >= steps.length ? (
              <span className="font-medium text-success">{steps[steps.length - 1]} · done</span>
            ) : (
              <>
                <span className="font-medium text-ink">{steps[current]}</span> · step {current + 1} of {steps.length}
              </>
            )}
          </>
        )}
      </p>
      {stopped && <p className="mt-2 hidden text-xs text-danger md:block">{stopped}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page actions                                                        */
/* ------------------------------------------------------------------ */

export interface PageAction {
  key: string;
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  /** primary: the one next step; danger: cancel, reject, remove */
  tone?: 'primary' | 'danger';
  busy?: boolean;
  /** Shown but not usable; `disabledReason` explains why (tooltip on desktop, line under the bar on phones) */
  disabled?: boolean;
  disabledReason?: string;
}

/**
 * A record's actions. Desktop: buttons in the header, danger ones last. Phone: the primary action
 * becomes a full-width bar above the tab bar, and everything else goes behind a "More" menu,
 * so a record never shows four wrapped buttons.
 */
export function PageActions({ actions }: { actions: PageAction[] }) {
  if (!actions.length) return null;
  const primary = actions.find((a) => a.tone === 'primary');
  const rest = actions.filter((a) => a !== primary);
  const ordered = [...rest.filter((a) => a.tone !== 'danger'), ...(primary ? [primary] : []), ...rest.filter((a) => a.tone === 'danger')];
  return (
    <>
      <div className="hidden flex-wrap items-center gap-2 md:flex">
        {ordered.map((a) => (
          <Button
            key={a.key}
            variant={a.tone === 'primary' ? 'primary' : a.tone === 'danger' ? 'danger' : 'secondary'}
            onClick={a.onClick}
            busy={a.busy}
            disabled={a.disabled}
            title={a.disabled ? a.disabledReason : undefined}
          >
            {a.icon && <a.icon className="size-4" />}
            {a.label}
          </Button>
        ))}
      </div>
      <div className="flex items-center gap-2 md:hidden">
        {rest.length > 0 && (
          <RowMenu
            label="More actions"
            items={rest.map((a) => ({ label: a.label, icon: a.icon, onSelect: a.onClick, danger: a.tone === 'danger', disabled: a.disabled }))}
            trigger={
              <button className="flex size-11 cursor-pointer items-center justify-center rounded-md border border-line-strong bg-surface text-ink shadow-sm active:bg-paper" aria-label="More actions" title="More actions">
                <MoreHorizontal className="size-5" />
              </button>
            }
          />
        )}
      </div>
      {primary && (
        <div data-primary-bar className="fixed inset-x-0 bottom-[var(--tabbar-h)] z-20 border-t border-line bg-surface/95 px-4 py-3 backdrop-blur md:hidden">
          <Button variant="primary" onClick={primary.onClick} busy={primary.busy} disabled={primary.disabled} className="w-full">
            {primary.icon && <primary.icon className="size-4" />}
            {primary.label}
          </Button>
          {primary.disabled && primary.disabledReason && <p className="mt-1.5 text-center text-xs text-ink-soft">{primary.disabledReason}</p>}
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Menus and tooltips                                                  */
/* ------------------------------------------------------------------ */

export interface MenuItem {
  label: ReactNode;
  icon?: LucideIcon;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
}

/** Row actions behind a ⋯ button, so a table row doesn't need four tiny buttons on a phone */
export function RowMenu({ items, label = 'Actions', trigger }: { items: MenuItem[]; label?: string; trigger?: ReactNode }) {
  return (
    <RDropdown.Root modal={false}>
      <RDropdown.Trigger asChild>
        {trigger ?? (
          <button className="flex size-8 cursor-pointer items-center justify-center rounded-md text-ink-faint hover:bg-ink/5 hover:text-ink max-sm:size-tap" aria-label={label}>
            <MoreHorizontal className="size-4" />
          </button>
        )}
      </RDropdown.Trigger>
      <RDropdown.Portal>
        <RDropdown.Content align="end" sideOffset={4} className="z-50 min-w-44 rounded-lg border border-line bg-surface-raised p-1 shadow-lg">
          {items.map((it, i) => (
            <RDropdown.Item
              key={i}
              disabled={it.disabled}
              onSelect={it.onSelect}
              className={clsx(
                'flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm outline-none select-none data-[disabled]:opacity-50 data-[highlighted]:bg-ink/5 max-sm:py-2.5',
                it.danger ? 'text-danger' : 'text-ink',
              )}
            >
              {it.icon && <it.icon className="size-4 text-ink-soft" aria-hidden />}
              {it.label}
            </RDropdown.Item>
          ))}
        </RDropdown.Content>
      </RDropdown.Portal>
    </RDropdown.Root>
  );
}

export function Tooltip({ content, children }: { content: ReactNode; children: ReactNode }) {
  return (
    <RTooltip.Provider delayDuration={300}>
      <RTooltip.Root>
        <RTooltip.Trigger asChild>{children}</RTooltip.Trigger>
        <RTooltip.Portal>
          <RTooltip.Content sideOffset={4} className="z-50 rounded-md bg-ink px-2 py-1 text-xs text-paper shadow-md">
            {content}
          </RTooltip.Content>
        </RTooltip.Portal>
      </RTooltip.Root>
    </RTooltip.Provider>
  );
}

/* ------------------------------------------------------------------ */
/* Toasts                                                              */
/* ------------------------------------------------------------------ */

interface Toast {
  id: number;
  message: string;
  tone: 'ok' | 'error';
}
const ToastContext = createContext<(message: string, tone?: 'ok' | 'error') => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((message: string, tone: 'ok' | 'error' = 'ok') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4000);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div
        className="pointer-events-none fixed right-4 bottom-[calc(var(--tabbar-h)+1rem)] left-4 z-[60] flex flex-col items-end gap-2 sm:left-auto"
        aria-live="polite"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={clsx('pointer-events-auto max-w-sm rounded-md px-3.5 py-2.5 text-sm shadow-lg max-sm:w-full', t.tone === 'ok' ? 'bg-ink text-paper' : 'bg-danger text-white')}
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

/* ------------------------------------------------------------------ */
/* Hooks                                                               */
/* ------------------------------------------------------------------ */

export function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Compact or comfortable table rows, remembered per device and per table */
export function useDensity(table: string): [boolean, (dense: boolean) => void] {
  const key = `igoal.dense.${table}`;
  const [dense, setDenseState] = useState(() => {
    try {
      return localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  const setDense = useCallback(
    (d: boolean) => {
      setDenseState(d);
      try {
        localStorage.setItem(key, d ? '1' : '0');
      } catch {
        // private mode
      }
    },
    [key],
  );
  return [dense, setDense];
}

/** true below the `md` breakpoint (768 px) */
export function useIsPhone(): boolean {
  const [phone, setPhone] = useState(() => (typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false));
  useEffect(() => {
    const m = window.matchMedia('(max-width: 767px)');
    const on = () => setPhone(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return phone;
}
